import React, { useEffect, useRef, useState } from 'react'
import { MdOutlineMenu } from 'react-icons/md'
import Spacer from '@Components/Utilities/Spacer'
import { TextButton, IconButton, SkeletonBox } from '@eduact/ed-components'
import { LogoutIcon } from './Assets/Icons'
import Avatar from '@Components/Utilities/Avatar/Avatar'
import {
	MainHeaderContainer,
	HeaderWrapper,
	LogoImageWrapper,
	RightSideWrapper,
	DesktopRHS,
	UserPrefsWrapper,
	UsernameChip,
	LogoutButton,
} from './Header.styled'
import CustomLink from '@Styles/CustomLink'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useDispatch } from 'react-redux'
import { logoutRequestAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GridItem } from '@eduact/ed-system'
import styled from 'styled-components'
import { useStudentScreenSize, useTheme } from '@eduact/student-theme'
import EdDrawer from './Components/EdDrawer/EdDrawer'
import routes from '@Constants/Routes'
import { useLayoutProvider } from '@Providers/LayoutProvider/LayoutProvider'
import WalletChip from './Components/Wallet/WalletChip'
import WalletPanel from './Components/Wallet/WalletPanel'
import { useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import { ProgressAPI } from '@Features/Progress/ProgressSlice/ProgressSlice'
import { setCurrentURL } from '@Redux/UtilsSlice/UtilsSlice'
import { defaultAssets } from '@Constants/assets.defaults'
import { excludedRedirections } from '@Providers/RoutingProvider/RoutingProvider'
import { Commands } from './Components/Commands/Commands'
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook'
import { useDeviceInfo } from '@eduact/utils'
import { saveManualDraft, manualVcWalletEvent } from '@Features/ManualVc/ManualVcDraft'
import { manualVcKey } from '@Features/ManualVc/ManualVc.api'

const hotKeysMap = {
	'Mac OS': { label: '⌘ + F', key: 'meta + f' },
	Windows: { label: 'Ctrl + F', key: 'ctrl + f' },
	Linux: { label: 'Ctrl + F', key: 'ctrl + f' },
}
const Header: React.FC = () => {
	const [fixed, setFixed] = useState(false)
	const headerRef = useRef<HTMLDivElement>(null)
	const { user, isAuth } = useAppSelector(state => state.auth)
	const { data } = useGetUserProfileQuery(undefined, {
		skip: !isAuth,
	})

	const { assets, assetsLoading } = useAppSelector(s => s.settings)

	const dispatch = useDispatch()
	const [t, i18n] = useTranslation('layouts', { keyPrefix: 'header' })
	const history = useNavigate()
	const location = useLocation()

	const { showWallet, fixedHeader, embed } = useLayoutProvider()
	const { app_wallet } = useAppSelector(s => s.settings)
	const { setTheme, theme } = useTheme()
	const [drawerOpen, setDrawerOpen] = useState(false)
	// Held here rather than inside the wallet component: the chip that opens the
	// panel now lives in the header row, while the panel still hangs off the
	// header container below it.
	const [walletOpened, setWalletOpened] = useState(false)
	const { isDesktop } = useStudentScreenSize()
	useEffect(() => {
		const open = (event: Event) => {
			const { amount, reference } = (event as CustomEvent).detail || {}
			// The mobile drawer mounts Wallet only after opening. Persist its initial selection first.
			if (user?.uuid && (amount || reference)) saveManualDraft(user.uuid, { amount: amount || 100, reference, key: manualVcKey() })
			if (isDesktop) setWalletOpened(true)
			else setDrawerOpen(true)
		}
		window.addEventListener(manualVcWalletEvent, open)
		return () => window.removeEventListener(manualVcWalletEvent, open)
	}, [isDesktop, user?.uuid])
	useEffect(() => {
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
		window.addEventListener('scroll', onWindowScroll, false)
		return () => {
			window.removeEventListener('scroll', onWindowScroll, false)
		}
	}, [])

	const { os } = useDeviceInfo({})

	const onWindowScroll = (e: Event) => {
		const scrollPosition = window.scrollY
		if (headerRef && headerRef.current != null) {
			if (scrollPosition > 300) {
				headerRef.current.style.zIndex = '99'
				setFixed(true)
			} else {
				headerRef.current.style.zIndex = ''
				setFixed(false)
			}
		}
	}
	const handleLogout = async () => {
		// Ends the session on the server, then clears local state. Previously this
		// only cleared localStorage — the credential stayed valid, so "sign out"
		// on a shared machine protected nothing.
		await dispatch(logoutRequestAction())
		dispatch(BaseApi.util.resetApiState())
		// ProgressAPI is a separate root with its own cache: without its own reset
		// the signed-out student's per-course progress (names included) survived
		// logout until cache collection got around to it.
		dispatch(ProgressAPI.util.resetApiState())
		setDrawerOpen(false)
		history('/')
	}
	const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter') return
		const { value } = e.target as HTMLInputElement
		if (value === '') {
			history(`/results?query=`)
		} else {
			history(`/results?query=${value}`)
		}
	}

	/* tenant/mribrahimfarid: the language switch is removed — the platform is
	   Arabic-only for this tenant (changeLanguage + toggle buttons dropped). */

	const handleAuthNavigation = (route: string) => {
		dispatch(setCurrentURL(location.pathname))
		history(route)
	}
	const { redirect: redirectCode } = useAppSelector(s => s.auth)

	const redirect = redirectCode && !excludedRedirections.includes(redirectCode)

	// Unchanged gating from the floating bubble: settings flag, per-route opt-out,
	// desktop only, and signed in — the old component did the isAuth check
	// internally, which also kept the panel from mounting for guests.
	const walletVisible = app_wallet && showWallet && isDesktop && isAuth

	const [openCommands, setOpenCommands] = useState(false)
	useHotkeys(
		hotKeysMap[os.name as keyof typeof hotKeysMap]?.key || 'Ctrl + F',
		e => {
			setOpenCommands(!openCommands)
		},
		{
			enabled: true,
			preventDefault: true,
		},
		[openCommands]
	)

	return (
		<MainHeaderContainer layoutConfigFixed={fixedHeader} fixed={fixed} ref={headerRef}>
			<Commands open={openCommands} onClose={setOpenCommands} />
			<HeaderWrapper>
				<EdDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} handleLogout={handleLogout} />
				<LogoImageWrapper>
					<GridItem gridColumn={{ sm: '1/3', lg: '1/2' }}>
						{/* <CustomLink width="70px" to={embed ? '' : '/'} display='block'> */}
						<LogoLink to={embed ? '' : '/'} display="block" disabled={!!redirect}>
							{assetsLoading && <SkeletonBox isLoading width="160px" height="42px" />}
							{!assetsLoading && <img alt="eduact" src={assets.logo ?? defaultAssets.logo} />}
						</LogoLink>
					</GridItem>
				</LogoImageWrapper>
				{/* <SearchInputWrapper>
					<SearchInputStyled>
						<AiOutlineSearch />
						<input
							// value={search}
							// onChange={e => e.preventDefault()}
							disabled={!!redirect}
							onKeyPress={handleSearch}
							placeholder={t('search_hint')}
						/>
					</SearchInputStyled>
				</SearchInputWrapper> */}
				<RightSideWrapper display={{ md: 'hidden' }}>
					<RightSideContainer>
						{/* tenant/mribrahimfarid: the command-bar button is removed from the
						    header; the palette stays reachable via its Ctrl+F hotkey. */}
						<IconButton onClick={() => setDrawerOpen(!drawerOpen)} padding={'0'} variant="dark" icon={<MdOutlineMenu size={'1.1rem'} />} />
					</RightSideContainer>
				</RightSideWrapper>
				<DesktopRHS>
					<UserPrefsWrapper>
						{!isAuth && (
							<>
								<TextButton
									textDecoration={'none'}
									onClick={() => handleAuthNavigation('/login')}
									variant="dark"
									padding={'0'}
									fontWeight={'bold'}
								>
									{t('login')}
								</TextButton>
								<Spacer />
								<TextButton
									textDecoration={'none'}
									onClick={() => handleAuthNavigation('/signup')}
									variant="dark"
									padding={'0'}
									fontWeight={'bold'}
								>
									{t('signup')}
								</TextButton>
							</>
						)}
						{walletVisible && <WalletChip onClick={() => setWalletOpened(true)} />}
						{isAuth && (
							<CustomLink to={routes.dashboard} disabled={!!redirect}>
								<UsernameChip>
									<Avatar img={data?.data?.profile_photo} avatarSize="small" />
									<Spacer mx={'6px'} />
									<span>{user?.username}</span>
								</UsernameChip>
							</CustomLink>
						)}
						{isAuth && !embed && (
							<LogoutButton onClick={() => handleLogout()}>
								<LogoutIcon />
								<span>{t('logout')}</span>
							</LogoutButton>
						)}
					</UserPrefsWrapper>
				</DesktopRHS>
			</HeaderWrapper>
			{walletVisible && <WalletPanel opened={walletOpened} onClose={() => setWalletOpened(false)} />}
		</MainHeaderContainer>
	)
}

export default Header

const RightSideContainer = styled.div`
	display: flex;
	justify-content: end;
	gap: 0.5rem;
	padding: 0 1rem;
	width: 100%;
	${({ theme }) => `${theme.mediaQueries.large}{
		gap: 1.5rem;
	}`};
`

const LogoLink = styled(CustomLink)<{ disabled?: boolean }>`
	align-items: center;
	display: flex;
	height: 42px;
	max-width: 160px;
	img {
		max-height: 100%;
		max-width: 100%;
		object-fit: contain;
	}
	pointer-events: ${p => p.disabled && 'none'};
`
